import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { setContactFormModalOpen } from '@/app/redux/slices/modalsSlice';
import {
  formatPrice,
  getBillingCycleLabel,
  getCouponValid,
  getPlanBreakdown,
  PLAN_DETAILS,
} from '@/common/utils/license';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import JSConfetti from 'js-confetti';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PaymentSuccessDialogueProps {
  isOpen: boolean;
  onClose: () => void;
}
export default function PaymentSuccessDialogue({
  isOpen,
  onClose,
}: PaymentSuccessDialogueProps) {
  const dispatch = useAppDispatch();

  const { organisation } = useOrganisation();

  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const license = organisation?.license;
  const licensePlan = license?.plan ?? 'trial';
  const seatCount = license?.seatCount ?? 0;
  const billingCycle = license?.billingCycle ?? '1year';
  const billingInfo = license?.billingInfo ?? null;
  const currency = license?.currency ?? 'USD';
  const coupon = license?.coupon ?? null;
  const planDetails = PLAN_DETAILS[licensePlan];

  useEffect(() => {
    if (!isOpen) return;

    const showConfetti = () => {
      if (!confettiCanvasRef.current) return;
      const confetti = new JSConfetti({ canvas: confettiCanvasRef.current });
      confetti.addConfetti({
        confettiColors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
        confettiRadius: 5,
        confettiNumber: 200,
      });
      confetti.addConfetti({
        emojis: ['🎉', '🪄', '✨'],
        emojiSize: 16,
        confettiNumber: 100,
      });
    };

    const timeout = setTimeout(showConfetti, 100);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  const couponValid = useMemo(
    () => getCouponValid(coupon?.appliedAt, billingCycle),
    [coupon, billingCycle],
  );

  const price = useMemo(() => {
    return getPlanBreakdown(
      licensePlan,
      seatCount,
      billingCycle,
      currency,
      coupon?.percentOff ?? null,
      couponValid,
    ).total;
  }, [licensePlan, seatCount, billingCycle, currency, coupon, couponValid]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {createPortal(
        <canvas
          ref={(el) => (confettiCanvasRef.current = el)}
          className="fixed inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 9999 }}
        />,
        document.body,
      )}
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="mr-1">🎉</span>
            Welcome aboard!
          </DialogTitle>
          <DialogDescription>
            Your Spellcaster subscription is now active!
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 border border-border rounded-sm p-4 text-sm font-medium">
          <span className="font-semibold">Plan</span>
          <span>{planDetails.name}</span>
          <span className="font-semibold">Learners</span>
          <span>{seatCount}</span>
          <span className="font-semibold">Billing Cycle</span>
          <span className="capitalize">
            {getBillingCycleLabel(billingCycle)}
          </span>
          <span className="font-semibold">Total</span>
          <span>{formatPrice(price, currency)}</span>
          <span className="font-semibold">Payment Method</span>
          <span>
            {billingInfo?.card
              ? `${billingInfo.card.brand} •••• ${billingInfo.card.last4}`
              : 'Invoice'}
          </span>
          <span className="font-semibold">Billing Email</span>
          <span>{billingInfo?.email}</span>
        </div>

        <div className="mb-2 text-sm text-muted-foreground flex flex-col gap-2">
          <span>
            If you are paying by invoice, you will receive an invoice to the
            nominated email shortly.
          </span>
          <span>
            You can update or cancel your subscription at any time in your{' '}
            <span className="underline cursor-pointer" onClick={onClose}>
              Billing & Subscription
            </span>{' '}
            settings. If you have any questions or feedback, please get in touch
            at{' '}
            <span
              className="underline cursor-pointer"
              onClick={() => {
                dispatch(setContactFormModalOpen(true));
              }}
            >
              rupert@spellcaster.education
            </span>
          </span>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Okay, got it!</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
